/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../../core/components';
import { CertInfoModel } from '../../../../../shared/models/certInfo.model';
import { CertModel, CertsApiService, LbErrorService } from '../../../../../shared';
import { Component, OnInit } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../../../core/models';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { LoadingService, LogService, UtilsService, ValidationService } from '../../../../../../../core/services';
import { nameValidation, subjectAltNameValidation } from '../../../../../../../core/directives';
import { forkJoin, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-create-csr',
    templateUrl: './addEditCSR.component.html',
    styleUrls: ['./addEditCSR.component.scss']
})

export class AddEditCSRComponent extends BaseComponent implements OnInit {
    /**
     * Create CSR from group
     */
    public createCSRForm: FormGroup;

    /**
     * Dropdown static content
     */
    public CSRKeyLengthArray: DropdownModel[] = [
        <DropdownModel>{ key: 2048, value: '2048 bits' },
        <DropdownModel>{ key: 4096, value: '4096 bits' }
    ];

    /**
     * Component embedded in wizard flag.
     */
    public componentEmbedded = false;

    /**
     * Store callback function passed in from parent.
     */
    public callBack: (...args: any[]) => void;

    /**
     * Store page title.
     */
    public pageTitle: string;

    /**
     * Store the CSR id.
     * @private
     */
    public id: string;

    /**
     * Store certs.
     * @private
     */
    private csrs: CertModel[];

    /**
     * Store initial form data.
     * @private
     */
    private initialData: any = {};

    /**
     * Store signature algorithms.
     * @private
     */
    private _signatureAlgorithms: any;

    /**
     * Store subject alts for the getter.
     * @private
     */
    private _subjectAlt: FormArray;

    /**
     * Store validator to make them available to the view.
     * @protected
     */
    protected readonly subjectAltValidation = subjectAltNameValidation;
    protected readonly Validators = Validators;

    constructor(
        private certsService: CertsApiService,
        private formBuilder: FormBuilder,
        private lbErrorService: LbErrorService,
        private loadingService: LoadingService,
        private logService: LogService,
        private route: ActivatedRoute,
        private router: Router,
        private utilsService: UtilsService
    ) {
        super();
    }

    /**
     * Signature algorithms setter.
     * @param signatureAlgorithms
     */
    set signatureAlgorithms(signatureAlgorithms: DropdownModel[]) {
        this._signatureAlgorithms = signatureAlgorithms;
    }

    /**
     * Signature algorithms getter.
     */
    get signatureAlgorithms() {
        return this._signatureAlgorithms;
    }

    /**
     * Getter for subjectAlts in the Create CSR form.
     */
    get subjectAlt(): FormArray {
        this._subjectAlt = this.createCSRForm.get('subjectAlt') as FormArray;

        return this._subjectAlt;
    }

    public ngOnInit(): void {
        this.id = this.componentEmbedded ? this.id : this.route.snapshot.params['id'];
        this.setPageTitle();
        this.setUpPageData();
        this.setupForm();
        this.setupFormSubscribers();
    }

    /**
     * Return to parent component.
     */
    public returnToParent(): void {
        if (this.componentEmbedded) {
            this.callBack();
        } else {
            this.router.navigate([this.id ? '../../../' : '../../'], { relativeTo: this.route }).then().catch();
        }
    }

    /**
     * Request CSR
     */
    public submitCSR(): Observable<any> {
        return new Observable((observer) => {
            const apiCall: string = this.id ? 'updateCSR' : 'createCSR';
            const cert = this.formatRequestBody();
            if (this.id) {
                cert['id'] = this.id;
            }

            this.certsService[apiCall](cert)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.returnToParent();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.loadingService.loadingButtonEmitter.next(false);
                        this.lbErrorService.pageWithErrors(this.createCSRForm, error.error.errors);
                        this.logService.error('SSLCertificateComponent: submitCSR', 'Error submitting data', error);
                        observer.error(error);
                        observer.complete();
                    },
                    complete: (): void => {
                        this.loadingService.loadingButtonEmitter.next(false);
                    }
                });
        });
    }

    /**
     * Format csr request body.
     * @private
     */
    private formatRequestBody(): any {
        const csrForm = this.createCSRForm.value;

        return {
            name: csrForm.name,
            certInfo: {
                commonName: csrForm.commonName,
                organisation: csrForm.organisation,
                organisationalUnit: csrForm.organisationalUnit,
                locality: csrForm.locality,
                state: csrForm.state,
                country: csrForm.country,
                subjectAlt: csrForm.subjectAlt.filter((item: string) => item !== ''),
                email: csrForm.email,
            },
            selfSigned: csrForm.selfSigned,
            keyLength: csrForm.keyLength,
            signatureAlgorithm: csrForm.signatureAlgorithm,
            days: csrForm.days
        };
    }

    /**
     * Set up data for the page.
     * @private
     */
    private setUpPageData(): void {
        const apiCalls = {
            csrs: this.certsService.getCSRs(),
            signatureAlgorithms: this.certsService.getSignatureAlgorithms()
        };

        if (this.id) {
            apiCalls['csr'] = this.certsService.getCSR(this.id);
        }

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    this.csrs = data['csrs'];
                    this.setupSignatureAlgorithmsDropdown(data['signatureAlgorithms']);
                    if (data['csr']) {
                        this.patchDataToForm(data['csr']);
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('AddEditCSRComponent: setUpPageData() error fetching data ', error.message);
                }
            });
    }

    /**
     * Set page title.
     * @private
     */
    private setPageTitle(): void {
        this.pageTitle = this.id ? 'certificate_edit_csr' : 'certificate_create_csr';
    }

    /**
     * Setup form.
     * @private
     */
    private setupForm(): void {
        this.createCSRForm = this.formBuilder.group({
            name: [null, [Validators.required, nameValidation]],
            commonName: [null, [Validators.required, nameValidation]],
            organisation: [null, [Validators.required, nameValidation]],
            organisationalUnit: [null, [Validators.required, nameValidation]],
            locality: [null, [Validators.required, nameValidation]],
            state: [null, [Validators.required, nameValidation]],
            country: [null, [Validators.required]],
            subjectAlt: new FormArray([new FormControl('', [Validators.required, subjectAltNameValidation])]),
            email: [null, [Validators.required, ValidationService.emailValidator]],
            keyLength: [null, [Validators.required]],
            signatureAlgorithm: [null, [Validators.required]],
            selfSigned: [false, Validators.required],
            days: [{ value: null, disabled: true }, Validators.required],
        });
    }

    /**
     * Setup form subscribers.
     * @private
     */
    private setupFormSubscribers(): void {
        const csrForm: FormGroup = this.createCSRForm;
        csrForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (form: any): void => {
                    this.utilsService.debounce('form', () => {
                        if (this.id) {
                            if (this.utilsService.hasFormDataChanged(this.initialData, form)) {
                                this.createCSRForm.markAsDirty();
                            } else {
                                this.createCSRForm.markAsPristine();
                            }
                        }
                        this.utilsService.validateCertsFormLabel(csrForm.get('name'), this.csrs, this.initialData.name);
                    }, 250);
                }
            });

        csrForm.get('selfSigned').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (checkboxValue: boolean): void => {
                    if (checkboxValue) {
                        csrForm.get('days').enable();
                    } else {
                        csrForm.get('days').disable();
                    }
                }
            });
    }

    /**
     * Patch data to form.
     * @param csr
     * @private
     */
    private patchDataToForm(csr: CertModel): void {
        const certInfo: CertInfoModel = csr.certInfo;
        this.patchValues(csr);
        this.setUpSubjectAlts(certInfo.subjectAlt);
        this.initialData = this.createCSRForm.value;
    }

    /**
     * Patch form values.
     * @param csr
     * @private
     */
    private patchValues(csr: CertModel): void {
        const certInfo: CertInfoModel = csr.certInfo;
        for (const field in certInfo) {
            if (certInfo.hasOwnProperty(field)) {
                if (field === 'subjectAlt') {
                    continue;
                }
                this.createCSRForm.get(field).setValue(certInfo[field]);
            }
        }

        this.createCSRForm.get('name').setValue(csr.name);
        this.createCSRForm.get('keyLength').setValue(csr.keyLength);
        this.createCSRForm.get('signatureAlgorithm').setValue(csr.signatureAlgorithm);
        this.createCSRForm.get('selfSigned').setValue(csr.selfSigned);
        this.createCSRForm.get('days').setValue(csr.days);
    }

    /**
     * Set up subject alts.
     * @param subjectAlts
     * @private
     */
    private setUpSubjectAlts(subjectAlts: string[]): void {
        this.subjectAlt.removeAt(0);
        for (const subject of subjectAlts) {
            this.subjectAlt.push(new FormControl(subject, [Validators.required, subjectAltNameValidation]));
        }
    }

    /**
     * Set up the signature algorithms dropdown.
     * @param data
     * @private
     */
    private setupSignatureAlgorithmsDropdown(data: any): void {
        const sigAlgs: DropdownModel[] = [];
        for (const signature of data) {
            sigAlgs.push(<DropdownModel>{ key: signature, value: signature.toUpperCase() });
        }

        this.signatureAlgorithms = sigAlgs;
    }
}
